import { useState } from 'react';
import MemberDetailDrawer from './MemberDetailDrawer';

/**
 * Organization Members - Team member management
 *
 * Features:
 * - Table view with columns: Name, Role, Meetings, Time, Last Active, Status
 * - Row actions: View Profile, Change Role, Remove
 * - Click row → opens MemberDetailDrawer
 */

export default function OrgMembers({ organization, members, isAdmin, onInvite }) {
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  // Filter members
  const filteredMembers = members.filter(member => {
    const matchesSearch = member.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         member.displayName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || member.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Never';
    const d = date instanceof Date ? date : date.toDate?.() || new Date(date);
    const now = new Date();
    const diff = now - d;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get role badge styling
  const getRoleBadge = (role) => {
    const styles = {
      owner: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      admin: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      member: 'bg-white/10 text-white/70 border-white/20'
    };
    return styles[role] || styles.member;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Team Members</h2>
          <p className="text-white/50 text-sm mt-1">{members.length} members in organization</p>
        </div>
        {isAdmin && (
          <button
            onClick={onInvite}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#06c] to-purple-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Invite Member
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-[#06c]/50 focus:ring-1 focus:ring-[#06c]/50 transition-all"
          />
        </div>

        {/* Role Filter */}
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#06c]/50 appearance-none cursor-pointer"
        >
          <option value="all" className="bg-[#1c1c1e]">All Roles</option>
          <option value="owner" className="bg-[#1c1c1e]">Owner</option>
          <option value="admin" className="bg-[#1c1c1e]">Admin</option>
          <option value="member" className="bg-[#1c1c1e]">Member</option>
        </select>
      </div>

      {/* Members Table */}
      <div className="backdrop-blur-xl bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/15 overflow-hidden">
        {/* Table Header */}
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-white/5 border-b border-white/10 text-white/50 text-xs font-medium uppercase tracking-wider">
          <div className="col-span-4">Member</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2 text-center">Meetings</div>
          <div className="col-span-2 text-center">Last Active</div>
          <div className="col-span-2 text-right">Status</div>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-white/5">
          {filteredMembers.length > 0 ? (
            filteredMembers.map((member) => (
              <div
                key={member.id}
                onClick={() => setSelectedMember(member)}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 hover:bg-white/5 cursor-pointer transition-colors"
              >
                {/* Member Info */}
                <div className="col-span-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#06c] to-purple-600 flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                    {member.email?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0">
                    <div className="text-white font-medium text-sm truncate">
                      {member.displayName || member.email?.split('@')[0] || 'Unknown'}
                    </div>
                    <div className="text-white/50 text-xs truncate">{member.email}</div>
                  </div>
                </div>

                {/* Role */}
                <div className="col-span-2 flex items-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadge(member.role)}`}>
                    {member.role || 'member'}
                  </span>
                </div>

                {/* Meetings Count */}
                <div className="col-span-2 flex items-center justify-center">
                  <span className="text-white/70 text-sm">{member.meetingsHosted || 0}</span>
                </div>

                {/* Last Active */}
                <div className="col-span-2 flex items-center justify-center">
                  <span className="text-white/50 text-sm">{formatDate(member.lastActive || member.joinedAt)}</span>
                </div>

                {/* Status */}
                <div className="col-span-2 flex items-center justify-end gap-2">
                  <span className={`w-2 h-2 rounded-full ${
                    member.status === 'active' ? 'bg-green-400' :
                    member.status === 'pending' ? 'bg-yellow-400' :
                    'bg-white/30'
                  }`} />
                  <span className={`text-sm capitalize ${
                    member.status === 'active' ? 'text-green-400' :
                    member.status === 'pending' ? 'text-yellow-400' :
                    'text-white/50'
                  }`}>
                    {member.status || 'active'}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-16">
              <div className="text-4xl mb-4">👥</div>
              <div className="text-white/50 text-sm">No members found</div>
              <div className="text-white/30 text-xs mt-1">
                {searchQuery ? 'Try a different search' : 'Invite team members to get started'}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Member Detail Drawer */}
      {selectedMember && (
        <MemberDetailDrawer
          member={selectedMember}
          organization={organization}
          isAdmin={isAdmin}
          onClose={() => setSelectedMember(null)}
        />
      )}
    </div>
  );
}
